import { useEffect, useState } from "react";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { SadieDashboardRangeSelect } from "@/components/tools/sadie-dashboard/SadieDashboardRangeSelect";
import { SadieExternalApplicationsCard } from "@/components/tools/sadie-dashboard/SadieExternalApplicationsCard";
import { SadieInternalApplicationsCard } from "@/components/tools/sadie-dashboard/SadieInternalApplicationsCard";
import { SadieNorthStarCard } from "@/components/tools/sadie-dashboard/SadieNorthStarCard";
import { SadieOptOutRateCard } from "@/components/tools/sadie-dashboard/SadieOptOutRateCard";
import { SadieReferralsCard } from "@/components/tools/sadie-dashboard/SadieReferralsCard";
import { SadieTextMessagesCard } from "@/components/tools/sadie-dashboard/SadieTextMessagesCard";
import { SadieWorkersTextedCard } from "@/components/tools/sadie-dashboard/SadieWorkersTextedCard";

import { useGetSadieDashboard } from "@/hooks/queries/useGetSadieDashboard";
import { useSetTitle } from "@/hooks/useSetTitle";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { SadieDashboardRange } from "@/types";

const DEFAULT_RANGE = SadieDashboardRange.LAST_30_DAYS;

export const ToolsSadieDashboardPage = () => {
  const [range, setRange] = useState(DEFAULT_RANGE);

  /* Database CRUD Operations */
  // Read
  const {
    data: dashboard,
    isError,
    isPending,
    isPlaceholderData,
  } = useGetSadieDashboard(range);

  /* React Hooks */
  useSetTitle({ title: "Tools | Sadie Dashboard" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.SADIE_DASHBOARD);
  }, []);

  /* Render */
  const render = () => {
    if (isPending) return <LoadingIndicator variant="medium" />;
    if (isError) {
      return <ErrorText errorMessage="Could not load the Sadie dashboard." />;
    }
    return (
      <Column className="gap-4">
        <SadieNorthStarCard
          isRefreshing={isPlaceholderData}
          range={dashboard.range}
          warmRelationships={dashboard.warmRelationships}
          warmShareOfTexted={dashboard.warmShareOfTexted}
        />
        <div
          className={cn(
            "grid grid-cols-1 gap-4 transition-opacity sm:grid-cols-2 xl:grid-cols-4",
            isPlaceholderData && "opacity-60",
          )}
        >
          <SadieWorkersTextedCard
            range={dashboard.range}
            workersFirstTexted={dashboard.workersFirstTexted}
          />
          <SadieTextMessagesCard
            range={dashboard.range}
            textMessages={dashboard.textMessages}
          />
          <SadieOptOutRateCard
            optOutRate={dashboard.optOutRate}
            range={dashboard.range}
          />
          <SadieInternalApplicationsCard
            internalApplications={dashboard.internalApplications}
            range={dashboard.range}
          />
          <SadieExternalApplicationsCard
            externalApplications={dashboard.externalApplications}
            range={dashboard.range}
          />
          <SadieReferralsCard
            range={dashboard.range}
            referrals={dashboard.referrals}
          />
        </div>
      </Column>
    );
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle>Sadie Dashboard</TopbarTitle>
      </TopbarContainer>
      <Column className="min-h-0 flex-1 gap-5 overflow-y-auto bg-slate-50 p-6">
        <Row className="flex-wrap items-end justify-between gap-4">
          <Column className="gap-1">
            <Text className="text-2xl font-semibold text-slate-900">Sadie</Text>
            <Text className="text-sm font-normal text-slate-500">
              Is Sadie reaching workers, learning what they want, and turning
              that into good matches?
            </Text>
          </Column>
          <SadieDashboardRangeSelect range={range} setRange={setRange} />
        </Row>
        {render()}
      </Column>
    </Column>
  );
};
